<script setup lang="ts" generic="T extends Record<string, string>">
import { inject, ref } from 'vue';
import { NxDateInput } from '../Inputs';
import {
  defaultEditCellFocusRef,
  i18nInjectionKey,
  onEditCellKeydown,
  PartialOrNull,
} from './internal';
import { default as NxTableColumn, type NxTableColumnProps } from './NxTableColumn.vue';

const props = withDefaults(
  defineProps<
    Omit<NxTableColumnProps<T, keyof T, string>, 'toCsvString'> & {
      /** value that will be used for the csv export */
      toCsvString?: NxTableColumnProps<T, keyof T, string>['toCsvString'];
    }
  >(),
  {
    filterable: true,
    resizeable: true,
    reorderable: true,
    sortable: true,
    editable: true,
    defaultVisible: true,
    layout: 'row',
    toCsvString: undefined,
  }
);

const modelValue = ref<string | null>(null);
function onQueryLoaded(value: string[]) {
  modelValue.value = value.length > 0 ? value[0] : null;
}

const i18nInjection = inject(i18nInjectionKey)!;

function toCsvString_(rowData: T, rowIndex: number) {
  if (props.toCsvString) {
    return props.toCsvString(rowData, rowIndex);
  }

  return rowData[props.columnId] ? i18nInjection.d(new Date(rowData[props.columnId]), 'short') : '';
}
</script>

<template>
  <!-- @vue-generic {T, keyof T, string} -->
  <NxTableColumn
    v-bind="{
      ...props,
      filterKind: props.filterKind ?? 'date',
      onQueryLoaded: props.onQueryLoaded ? props.onQueryLoaded : onQueryLoaded,
      toCsvString: toCsvString_,
    }"
  >
    <template v-if="$slots.summaryCell" #summaryCell="summaryProps">
      <slot name="summaryCell" v-bind="summaryProps"></slot>
    </template>
    <template v-if="$slots.headerCell" #headerCell>
      <slot name="headerCell"></slot>
    </template>
    <template
      #filterCell="{
        setFilterValue,
        disabled,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="filterCell"
        v-bind="{
          setFilterValue,
          disabled,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <NxDateInput
          :ref="defaultEditCellFocusRef(registerFocus)"
          :model-value="modelValue"
          height="xs"
          placeholder=""
          :disabled="disabled"
          @update:model-value="
            (date: string | null) => {
              setFilterValue(date !== null ? [date] : []);
              modelValue = date;
            }
          "
          @keydown="
            (e: KeyboardEvent) =>
              onEditCellKeydown(e, {
                focusAbove,
                focusBelow,
                focusLeft,
                focusRight,
                inputType: 'text',
              })
          "
        ></NxDateInput>
      </slot>
    </template>
    <template #cell="{ rowData, rowIndex }">
      <slot name="cell" v-bind="{ rowData, rowIndex }">
        <span class="one-line">
          {{ toCsvString_(rowData, rowIndex) }}
        </span>
      </slot>
    </template>
    <template #createCell="{ partialRowData, setPartialRowData }">
      <slot name="createCell" v-bind="{ partialRowData, setPartialRowData }">
        <!-- The staged value, NOT a hard-coded `null`: a date field renders what
             its model holds, so a bound `null` left the cell showing its
             placeholder after the user had picked a day. The value reached
             `onCreate` all along — it just looked like it had been dropped
             (I-1160). A text or number create cell never showed the defect
             because the typed characters live in the DOM, not in the model.
             `partialRowData` is the row NxTable is assembling; it clears it on
             save and on cancel, so the next create row starts empty by itself. -->
        <NxDateInput
          :model-value="partialRowData[props.columnId] ?? null"
          @update:model-value="
            (date: string | null) => {
              setPartialRowData({ [props.columnId]: date } as PartialOrNull<T>);
            }
          "
        ></NxDateInput>
      </slot>
    </template>
    <template
      #editCell="{
        rowData,
        rowIndex,
        onEdit,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="editCell"
        v-bind="{
          rowData,
          rowIndex,
          onEdit,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <div class="nx-ghost-cell">
          <!-- `?? null` on model-value: a row can be MISSING the key entirely,
               not just hold null — the GraphQL read omits null fields, so a
               nullable date column (employee_skills.expires_at) arrives as
               undefined and NxDateInput's modelValue (String | Null) warned on
               every such row. An absent date is "no date", i.e. null. -->
          <NxDateInput
            :ref="defaultEditCellFocusRef(registerFocus)"
            :model-value="rowData[props.columnId] ?? null"
            @update:model-value="
              (date: string | null) => {
                onEdit({ [props.columnId]: date } as Partial<T>);
              }
            "
            @keydown="
              (e: KeyboardEvent) =>
                onEditCellKeydown(e, {
                  focusAbove,
                  focusBelow,
                  focusLeft,
                  focusRight,
                  // TODO: check if correct
                  inputType: 'text',
                })
            "
          ></NxDateInput>
        </div>
      </slot>
    </template>
  </NxTableColumn>
</template>

<style scoped>
.one-line {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  /* Cut long values with an ellipsis, never mid-letter. */
  text-overflow: ellipsis;
}
</style>
